import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideCheckNoChangesConfig,
  provideZonelessChangeDetection,
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import moment from 'moment-timezone';

import { lastValueFrom } from 'rxjs';
import { version } from '../package.json';
import { MeService, OpenAPI } from './api';
import { AuthService } from './app/states/auth/services/auth.service';

import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';

import { AppComponent } from './app/app.component';

// angular-highcharts renders with the ESM build, so modules register on that instance.
import 'highcharts/esm/modules/stock.src';
import 'highcharts/esm/modules/xrange.src';
import 'highcharts/esm/modules/pattern-fill.src';
import 'highcharts/esm/modules/accessibility.src';

import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { APP_ROUTES } from './app/app.routes';
import { FormatConfigurations } from './app/core/configurations/FormatConfigurations';
import { OtherConstants } from './app/core/configurations/OtherConstants';
import { UserIdleConfig } from './app/core/services/user-idle/user-idle.config';
import { ServerErrorInterceptor } from './app/core/interceptors';
import { environment } from './environments/environment';

if (environment.production) {
  Sentry.init({
    dsn: environment && environment?.sentryDsn ? environment?.sentryDsn : '',
    // Release health (crash-free sessions/users, adoption) is per release.
    release: `dza-portal@${version}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      // v11 opens one session per page load; renew it per route as before.
      Sentry.browserSessionIntegration({ lifecycle: 'route' }),
    ],
    // v11 collects IPs and cookies by default; keep them out as before.
    dataCollection: { userInfo: false, cookies: false },
    // v11 attaches synthetic stacks to string errors (our HTTP errors), which
    // would regroup existing issues.
    attachStacktrace: false,
    // 100% used up the monthly quota around the 25th.
    tracesSampleRate: 0.2,
    beforeSend: (event, hint) => {
      // Status 0 means the browser never got a response at all (offline,
      // sleeping machine, aborted request, closed tab). There is nothing to
      // act on, so keep it out of Sentry.
      const error = hint?.originalException;
      if (error instanceof HttpErrorResponse && error.status === 0) {
        return null;
      }
      return event;
    },
  });
  enableProdMode();
}

// Times are the vehicles' local times wherever the viewer sits, so every moment -
// pickers, day edges sent to the API, displayed values - lives in one zone.
moment.tz.setDefault(OtherConstants.TimeZone);

OpenAPI.BASE = environment.apiUrl;
OpenAPI.WITH_CREDENTIALS = true;

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    // Development only: throw when a template shows a value that changed
    // without a signal / markForCheck notification. Every such error is a
    // missed zoneless conversion.
    ...(environment.production
      ? []
      : [provideCheckNoChangesConfig({ exhaustive: true })]),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true,
    },
    {
      provide: UserIdleConfig,
      useValue: {
        idle: environment.userIdleTimeout,
        timeout: 3600,
        ping: 120,
      },
    },
    // Routes are lazy chunks so the first paint stays small; preloading fetches
    // the rest right after startup so a menu click never waits on the network.
    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withPreloading(PreloadAllModules),
    ),
    { provide: LOCALE_ID, useValue: 'cs-CZ' },
    { provide: MAT_DATE_LOCALE, useValue: 'cs-CZ' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      // Full month name in every calendar header ("červenec 2026").
      provide: MAT_DATE_FORMATS,
      useValue: {
        ...MAT_MOMENT_DATE_FORMATS,
        display: {
          ...MAT_MOMENT_DATE_FORMATS.display,
          monthYearLabel: FormatConfigurations.CalendarMonthYearFormat,
        },
      },
    },
    {
      // Suppress the "Previous/Next month" (and year) tooltips on the nav arrows
      // across all datepickers.
      provide: MatDatepickerIntl,
      useFactory: () => {
        const intl = new MatDatepickerIntl();
        intl.prevMonthLabel = '';
        intl.nextMonthLabel = '';
        intl.prevYearLabel = '';
        intl.nextYearLabel = '';
        intl.prevMultiYearLabel = '';
        intl.nextMultiYearLabel = '';
        return intl;
      },
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    provideAppInitializer(async () => {
      inject(Sentry.TraceService);
      const meService = inject(MeService);
      const authService = inject(AuthService);
      try {
        const meResponse = await lastValueFrom(meService.me());
        authService.signIn(meResponse);
      } catch {
        // Not authenticated — authGuard will redirect to sign-in
      }
    }),
  ],
}).catch((err) => console.error(err));
